import { queryOptions } from '@tanstack/react-query';

import { parseSchema } from '@repo/utils/parse-schema';
import { minutesToMilliseconds } from '@repo/utils/time';

import type {
  CreateRoleBody,
  PagedResult,
  RegisterPersonBody,
  RegisterServiceUserBody,
} from '~/services/authorization/types.ts';
import {
  featureSchema,
  organizationProjectSchema,
  organizationSchema,
  pagedPersonUsersSchema,
  pagedServiceUsersSchema,
  projectRoleSchema,
  projectSchema,
  registerPersonResponseSchema,
  registerPersonSchema,
  registerServiceUserResponseSchema,
  registerServiceUserSchema,
  roleSchema,
  userMeSchema,
} from '~/services/authorization/types.ts';
import { http } from '~/services/http.ts';

const apiBase = `${import.meta.env.VITE_API_BASE}/authorization`;
const defaultUserPageSize = 25;
const pickerPageSize = 100;

export type GetUsersParams = {
  searchText?: string;
  page?: number;
  pageSize?: number;
  // Server-side role filtering: by a specific role id, or by role type (used for the
  // synthetic "admin" filters that have no entry in the custom-role list).
  roleId?: string | undefined;
  roleType?: 'SuperAdmin' | 'Admin' | 'Custom' | undefined;
  // Server-side sorting so ordering spans the whole result set, not just one page.
  sortBy?: 'name' | 'email' | undefined;
  sortDescending?: boolean | undefined;
};

function buildUserSearchParams(params: GetUsersParams = {}) {
  const searchParams = new URLSearchParams();

  if (params.searchText?.trim()) {
    searchParams.set('searchText', params.searchText.trim());
  }
  if (params.page !== undefined) {
    searchParams.set('page', `${params.page}`);
  }
  if (params.pageSize !== undefined) {
    searchParams.set('pageSize', `${params.pageSize}`);
  }
  if (params.roleId?.trim()) {
    searchParams.set('roleId', params.roleId.trim());
  }
  if (params.roleType) {
    searchParams.set('roleType', params.roleType);
  }
  if (params.sortBy) {
    searchParams.set('sortBy', params.sortBy);
  }
  if (params.sortDescending) {
    searchParams.set('sortDescending', 'true');
  }

  return searchParams;
}

async function getAllUserPages<T>(fetchPage: (params: GetUsersParams) => Promise<PagedResult<T>>) {
  const firstPage = await fetchPage({ page: 1, pageSize: pickerPageSize });

  if (firstPage.totalPages <= 1) {
    return firstPage.items;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
      fetchPage({ page: index + 2, pageSize: pickerPageSize })),
  );

  return firstPage.items.concat(remainingPages.flatMap(page => page.items));
}

async function getCurrentUser() {
  const user = await http(`${apiBase}/users/me`);

  return parseSchema(userMeSchema, user);
}

export function queryCurrentUser() {
  return queryOptions({
    queryKey: ['current-user'],
    queryFn: getCurrentUser,
    staleTime: minutesToMilliseconds(30),
  });
}

async function getOrgById(orgId: string) {
  const org = await http(`${apiBase}/organizations/${orgId}`);

  return parseSchema(organizationSchema, org);
}

export function queryOrganizationById(orgId: string) {
  return queryOptions({
    queryKey: ['organization', orgId],
    queryFn: async () => getOrgById(orgId),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(2),
  });
}

async function getProjects(orgId: string) {
  const projects = await http(`${apiBase}/organizations/${orgId}/projects`);

  return parseSchema(organizationProjectSchema.array(), projects);
}

export function queryProjects(orgId: string) {
  return queryOptions({
    queryKey: ['projects', orgId],
    queryFn: async () => getProjects(orgId),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(30),
  });
}

async function getProjectById(projectId: string) {
  const project = await http(`${apiBase}/projects/${projectId}`);

  const parsed = parseSchema(projectSchema, project);

  if (parsed.permissions.length === 0) {
    throw new Error('Not authorized', { cause: { status: 401 } });
  }

  return parsed;
}

export function queryProjectById(projectId: string) {
  return queryOptions({
    queryKey: ['projects', projectId],
    queryFn: async () => getProjectById(projectId),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(30),
  });
}

async function getOrganizationFeatures(orgId: string) {
  const features = await http(`${apiBase}/organizations/${orgId}/features`);

  return parseSchema(featureSchema.array(), features).filter(
    feature => feature.scope === 'Organization' && feature.audience === 'External',
  );
}

export function queryOrganizationFeatures(orgId: string) {
  return queryOptions({
    queryKey: ['organization-features', orgId],
    queryFn: async () => getOrganizationFeatures(orgId),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(30),
  });
}

async function getProjectFeatures(projectId: string) {
  const features = await http(`${apiBase}/projects/${projectId}/features`);

  return parseSchema(featureSchema.array(), features).filter(
    feature => feature.scope === 'Project' && feature.audience === 'External',
  );
}

export function queryProjectFeatures(projectId: string) {
  return queryOptions({
    queryKey: ['project-features', projectId],
    queryFn: async () => getProjectFeatures(projectId),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(30),
  });
}

async function getOrganizationRoles(orgId: string) {
  const roles = await http(`${apiBase}/organizations/${orgId}/roles`);

  return parseSchema(roleSchema.array(), roles)
    .toSorted((a, b) => a.name.localeCompare(b.name));
}

export function queryOrganizationRoles(orgId: string) {
  return queryOptions({
    queryKey: ['roles', orgId],
    queryFn: async () => getOrganizationRoles(orgId),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(30),
  });
}

async function getProjectRoles(projectId: string) {
  const roles = await http(`${apiBase}/projects/${projectId}/roles`);

  return parseSchema(projectRoleSchema.array(), roles)
    .toSorted((a, b) => a.name.localeCompare(b.name));
}

export function queryProjectRoles(projectId: string) {
  return queryOptions({
    queryKey: ['roles', projectId],
    queryFn: async () => getProjectRoles(projectId),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(30),
  });
}

async function getOrganizationUsers(orgId: string, params: GetUsersParams = {}) {
  const searchParams = buildUserSearchParams(params);
  const url = `${apiBase}/organizations/${orgId}/users/persons${searchParams.size ? `?${searchParams.toString()}` : ''}`;
  const org = await http(url);

  return parseSchema(pagedPersonUsersSchema, org);
}

export function queryOrganizationUsers(orgId: string, params: GetUsersParams = {}) {
  const mergedParams = {
    page: 1,
    pageSize: defaultUserPageSize,
    ...params,
  };

  return queryOptions({
    queryKey: ['organization-users', orgId, mergedParams],
    queryFn: async () => getOrganizationUsers(orgId, mergedParams),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(2),
  });
}

export function queryAllOrganizationUsers(orgId: string, searchText = '') {
  return queryOptions({
    queryKey: ['organization-users-all', orgId, searchText.trim()],
    queryFn: async () => getAllUserPages(params => getOrganizationUsers(orgId, { ...params, searchText })),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(2),
  });
}

async function getOrganizationServiceUsers(orgId: string, params: GetUsersParams = {}) {
  const searchParams = buildUserSearchParams(params);
  const url = `${apiBase}/organizations/${orgId}/users/services${searchParams.size ? `?${searchParams.toString()}` : ''}`;
  const org = await http(url);

  return parseSchema(pagedServiceUsersSchema, org);
}

export function queryOrganizationServiceUsers(orgId: string, params: GetUsersParams = {}) {
  const mergedParams = {
    page: 1,
    pageSize: defaultUserPageSize,
    ...params,
  };

  return queryOptions({
    queryKey: ['organization-service-users', orgId, mergedParams],
    queryFn: async () => getOrganizationServiceUsers(orgId, mergedParams),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(2),
  });
}

export function queryAllOrganizationServiceUsers(orgId: string, searchText = '') {
  return queryOptions({
    queryKey: ['organization-service-users-all', orgId, searchText.trim()],
    queryFn: async () => getAllUserPages(params => getOrganizationServiceUsers(orgId, { ...params, searchText })),
    enabled: Boolean(orgId),
    staleTime: minutesToMilliseconds(2),
  });
}

async function getProjectUsers(projectId: string, params: GetUsersParams = {}) {
  const searchParams = buildUserSearchParams(params);
  const url = `${apiBase}/projects/${projectId}/users/persons${searchParams.size ? `?${searchParams.toString()}` : ''}`;
  const project = await http(url);

  return parseSchema(pagedPersonUsersSchema, project);
}

export function queryProjectUsers(projectId: string, params: GetUsersParams = {}) {
  const mergedParams = {
    page: 1,
    pageSize: defaultUserPageSize,
    ...params,
  };

  return queryOptions({
    queryKey: ['project-users', projectId, mergedParams],
    queryFn: async () => getProjectUsers(projectId, mergedParams),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(2),
  });
}

export function queryAllProjectUsers(projectId: string, searchText = '') {
  return queryOptions({
    queryKey: ['project-users-all', projectId, searchText.trim()],
    queryFn: async () => getAllUserPages(params => getProjectUsers(projectId, { ...params, searchText })),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(2),
  });
}

async function getProjectServiceUsers(projectId: string, params: GetUsersParams = {}) {
  const searchParams = buildUserSearchParams(params);
  const url = `${apiBase}/projects/${projectId}/users/services${searchParams.size ? `?${searchParams.toString()}` : ''}`;
  const project = await http(url);

  return parseSchema(pagedServiceUsersSchema, project);
}

export function queryProjectServiceUsers(projectId: string, params: GetUsersParams = {}) {
  const mergedParams = {
    page: 1,
    pageSize: defaultUserPageSize,
    ...params,
  };

  return queryOptions({
    queryKey: ['project-service-users', projectId, mergedParams],
    queryFn: async () => getProjectServiceUsers(projectId, mergedParams),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(2),
  });
}

export function queryAllProjectServiceUsers(projectId: string, searchText = '') {
  return queryOptions({
    queryKey: ['project-service-users-all', projectId, searchText.trim()],
    queryFn: async () => getAllUserPages(params => getProjectServiceUsers(projectId, { ...params, searchText })),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(2),
  });
}

export async function createOrganizationRole({ parentId, body }: { parentId: string; body: CreateRoleBody }) {
  return http(`${apiBase}/organizations/${parentId}/roles`, { method: 'POST', body });
}

export async function createProjectRole({ parentId, body }: { parentId: string; body: CreateRoleBody }) {
  return http(`${apiBase}/projects/${parentId}/roles`, { method: 'POST', body });
}

type RoleScope = {
  parentId: string;
  type: 'organization' | 'project';
};

export async function editRole({ roleId, body, parentId, type }: { roleId: string; body: CreateRoleBody } & RoleScope) {
  const scope = type === 'organization' ? 'organizations' : 'projects';

  return http(`${apiBase}/${scope}/${parentId}/roles/${roleId}`, { method: 'PATCH', body });
}

export async function deleteRole({ roleId, parentId, type }: { roleId: string } & RoleScope) {
  const scope = type === 'organization' ? 'organizations' : 'projects';

  return http(`${apiBase}/${scope}/${parentId}/roles/${roleId}`, { method: 'DELETE' });
}

export async function setOrganizationRoles({ parentId, userId, roles }: { parentId: string; userId: string; roles: string[] }) {
  return http(`${apiBase}/organizations/${parentId}/users/${userId}/roles`, { method: 'PUT', body: { roleIds: roles } });
}

export async function setProjectRoles({ parentId, userId, roles }: { parentId: string; userId: string; roles: string[] }) {
  return http(`${apiBase}/projects/${parentId}/users/${userId}/roles`, { method: 'PUT', body: { roleIds: roles } });
}

export async function assignOrganizationRolesBulk({ parentId, users, roles }: { parentId: string; users: string[]; roles: string[] }) {
  return http(`${apiBase}/organizations/${parentId}/roles/assign-roles`, { method: 'POST', body: { userIds: users, roleIds: roles } });
}

export async function assignProjectRolesBulk({ parentId, users, roles }: { parentId: string; users: string[]; roles: string[] }) {
  return http(`${apiBase}/projects/${parentId}/roles/assign-roles`, { method: 'POST', body: { userIds: users, roleIds: roles } });
}

export async function unassignOrganizationRolesBulk({ parentId, users, roles }: { parentId: string; users: string[]; roles: string[] }) {
  return http(`${apiBase}/organizations/${parentId}/roles/unassign-roles`, { method: 'POST', body: { userIds: users, roleIds: roles } });
}

export async function unassignProjectRolesBulk({ parentId, users, roles }: { parentId: string; users: string[]; roles: string[] }) {
  return http(`${apiBase}/projects/${parentId}/roles/unassign-roles`, { method: 'POST', body: { userIds: users, roleIds: roles } });
}

export async function registerOrganizationPerson({ parentId, body }: { parentId: string; body: RegisterPersonBody }) {
  const parsed = parseSchema(registerPersonSchema, body);
  const created = await http(`${apiBase}/organizations/${parentId}/users/register/person`, { method: 'POST', body: parsed });

  return parseSchema(registerPersonResponseSchema, created);
}

export async function registerOrganizationServiceUser({ parentId, body }: { parentId: string; body: RegisterServiceUserBody }) {
  const parsed = parseSchema(registerServiceUserSchema, body);
  const created = await http(`${apiBase}/organizations/${parentId}/users/register/service-account`, { method: 'POST', body: parsed });

  return parseSchema(registerServiceUserResponseSchema, created);
}
